import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  fetchActivityAlerts,
  fetchAlertsFreshness,
  SUCCESS_ALERT_TYPES,
  type ActivityAlertType,
  type AlertSeverity,
  type IActivityAlertItem,
} from '@apis/activityAlerts';
import { fetchSchedules } from '@apis/schedule';
import { scheduleListKey } from '@constants/queryKeys';
import { useActiveScheduleNumber } from '@hooks/useActiveSchedule';
import { useNow } from '@hooks/useNow';
import { useWindowedList } from '@hooks/useWindowedList';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { activityItemKey, issueItemKey } from '../../ActivitiesPage/model/listItem';
import { formatShortDate, currentLang, APP_DEFAULT_TZ } from '@utils/dateUtils';
import { AlertIcon, CheckCircleIcon, ChevronDownIcon, ClockIcon, RefreshIcon, ZapIcon } from '@utils/icons';
import { SafetyShieldFilledIcon } from '@components/Pages/IssuesPage/Board/icons';
import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { groupItemsByDay, isTimestampToday, todayKey, type IFeedDayGroup } from './feedDayGroups';
import { groupAlertsByType, type IAlertRow } from './alertGrouping';
import { detailLine } from './alertDetailLine';
import { DashboardCard } from './_shared/DashboardCard';
import { AlertDrawerHost } from './AlertDrawerHost';
import styles from './activityAlertsCard.module.css';

/* Activity Alerts — half-width card fed by GET /api/sites/:siteId/activity-alerts.
   Replaces the retired mock "Actions & Activities" feed card, reusing its
   shape: a query with staleTime/gcTime: Infinity + refetchOnWindowFocus:
   false, a header line stating the nightly refresh schedule + how stale this
   view is, day-sectioned rows windowed 20-at-a-time via an
   IntersectionObserver sentinel, a bounded enter animation for
   freshly-arrived rows, and a today-only quick filter. What's different here:
   rows carry no actor (alerts have no author), and icon/pill colour comes
   from severity instead of entity kind.

   Freshness is reader-controlled, never automatic: the list the reader is
   looking at is only replaced when THEY ask, and NOTHING here runs on a
   timer. A lightweight freshness probe (two timestamps, no alert payload) is
   fetched only at moments the reader causes — page load and tab refocus.
   When it shows an alert newer than anything in this view, the header offers
   "changes are available — refresh" (plus an always-present refresh button),
   and clicking either pulls the new list.

   Body layout (System Alerts): three independently collapsible sections —
   Activities, Issues, Deliveries & Stock — each carrying its OWN day
   headings (Today/Yesterday/dated) and running its own copy of the
   filter → day-group → type-group →
   window pipeline below, scoped to its own partition of the feed. There is
   no picker: all three sections are visible at once, by default expanded. */

/** Members listed in a group row's hover tooltip before "+N more". */
const GROUP_TIP_MAX = 8;

/** How long a freshly-arrived row's enter animation plays before its
 *  "entering" marker is cleared. */
const ENTER_ANIMATION_MS = 500;

/** Stable row identity — alerts have no id of their own. Issue alerts all carry
 *  an empty taskId (they belong to an issue, not an activity), so several
 *  issues overdue from the same sweep would otherwise collide on
 *  `${taskId}:${firedAt}:${type}`; issueId disambiguates them. */
function rowKey(alert: IActivityAlertItem): string {
  return `${alert.issueId ?? alert.taskId}:${alert.firedAt}:${alert.type}`;
}

const SEVERITY_ICON: Record<AlertSeverity, () => ReactElement> = {
  critical: () => <AlertIcon size={18} />,
  warning: () => <ClockIcon size={18} />,
  info: () => <CheckCircleIcon size={18} />,
};

/** A section header's today-count badge — "+12" to hint "this many new
 *  today", always shown, including "+0". */
const todayBadge = (count: number): string => `+${count}`;

/** The row pill's label. Issue types use the SHORT form ("Overdue", not "Issue
 *  overdue") — the Issues section already gives the "issue" context, so the
 *  prefix is redundant on the pill. Activity types keep the full `types` label
 *  so their names stay unambiguous. */
const pillLabel = (t: (key: string) => string, type: ActivityAlertType): string =>
  (type.startsWith('issue-')
    ? t(`dashboard.activityAlerts.typesShort.${type}`)
    : t(`dashboard.activityAlerts.types.${type}`));

/** The three Deliveries & Stock types the logistics sweep detector produces
 *  (`logistics-detector.ts`). They carry `scheduleNumber: ''` (they belong to
 *  inventory/deliveries, not a schedule task) and deep-link into Logistics
 *  rather than Activities. */
const LOGISTICS_ALERT_TYPES: ReadonlySet<ActivityAlertType> = new Set([
  'stock-gap', 'stock-ran-out', 'delivery-late', 'stock-untracked', 'supplier-declined',
]);

function isLogisticsAlertType(type: ActivityAlertType): boolean {
  return LOGISTICS_ALERT_TYPES.has(type);
}

/** The card's three collapsible sections. Order is fixed (spec §4):
 *  Activities, Issues, Deliveries & Stock. */
type AlertSectionId = 'activities' | 'issues' | 'logistics';

/** Which section an alert belongs to, from its TYPE alone (not its issueId) —
 *  logistics types first (they're a small closed set), then the `issue-*`
 *  prefix, everything else is an activity. */
function sectionIdForType(type: ActivityAlertType): AlertSectionId {
  if (isLogisticsAlertType(type)) return 'logistics';
  if (type.startsWith('issue-')) return 'issues';
  return 'activities';
}

/** The IListItem key (`act:<taskId>` / `iss:<issueId>`) AlertDrawerHost
 *  resolves a single alert row's drawer against. */
function alertItemKey(alert: IActivityAlertItem): string {
  return alert.issueId ? issueItemKey(alert.issueId) : activityItemKey(alert.taskId);
}

/** "View in page ›" — the filtered Activities page on its Daily List (the
 *  alert narrows list rows; the page opens on the Work Plan). An in-app link in
 *  the same tab (a modifier-click still opens a new one). */
function alertGroupHref(siteId: string | undefined, type: ActivityAlertType, dayKey: string): string {
  return `/activities/${siteId}?view=list&alertType=${encodeURIComponent(type)}&alertDay=${encodeURIComponent(dayKey)}`;
}

/** Deliveries & Stock rows deep-link into Logistics instead of the Activities
 *  page — same in-app link as `alertGroupHref`/"View in page ›", just a
 *  different destination + label. */
function alertViewHref(
  siteId: string | undefined,
  type: ActivityAlertType,
  dayKey: string,
  /** Every material the row stands for — one for a single alert, all of them
   *  for a group. Logistics filters every panel (inventory, untracked,
   *  deliveries) off the same `?q=`, and that query ORs its comma-separated
   *  terms, so a group of three lands on exactly those three rows rather than
   *  dumping the reader at the top of the page.
   *
   *  Each name is QUOTED, which makes it match whole names only. Material names
   *  nest — an unquoted `Waterproof sheet` also matches `Demo Waterproof Sheet`
   *  — so a link meaning "these three rows" would otherwise show four. */
  materialNames: Array<string | undefined> = [],
): string {
  if (!isLogisticsAlertType(type)) return alertGroupHref(siteId, type, dayKey);
  const named = [...new Set(materialNames.filter((name): name is string => Boolean(name)))];
  const query = named.length > 0
    ? `?q=${encodeURIComponent(named.map((name) => `"${name}"`).join(', '))}`
    : '';
  return `/logistics/${siteId}${query}`;
}

function alertViewLabel(t: (key: string) => string, type: ActivityAlertType): string {
  return isLogisticsAlertType(type)
    ? t('dashboard.activityAlerts.group.viewInLogistics')
    : t('dashboard.activityAlerts.group.viewInPage');
}

interface IAlertRowTitleProps {
  title: string;
  isCritical?: boolean;
  isSafety?: boolean;
  t: (key: string) => string;
}

/** A row's title with its inline markers — the critical-path zap and the safety
 *  shield. Shared by the single row and the expanded group-member row so the
 *  markup (and its ellipsis-truncation) can't drift between the two. */
function AlertRowTitle({ title, isCritical, isSafety, t }: IAlertRowTitleProps) {
  return (
    <span className={styles.itemTitle}>
      <span className={styles.titleText}>{title}</span>
      {isCritical && (
        <span
          className={styles.criticalMark}
          title={t('dashboard.activityAlerts.criticalPath')}
          aria-label={t('dashboard.activityAlerts.criticalPath')}
        >
          <ZapIcon size={12} />
        </span>
      )}
      {isSafety && (
        <span
          className={styles.safetyIcon}
          title={t('dashboard.activityAlerts.safetyTag')}
          aria-label={t('dashboard.activityAlerts.safetyTag')}
        >
          <SafetyShieldFilledIcon size={15} gradient />
        </span>
      )}
    </span>
  );
}

interface IAlertPillProps {
  pillClassName: string;
  label: string;
}

/** The severity/type pill at the right of a row. Shared by the single and group
 *  rows so the `pill + severity-class` composition lives in one place. */
function AlertPill({ pillClassName, label }: IAlertPillProps) {
  return <span className={`${styles.pill} ${pillClassName}`}>{label}</span>;
}

function severityClasses(severity: AlertSeverity, type: ActivityAlertType): { icon: string; pill: string } {
  // A completion is a positive event, not mere information — it gets the
  // success green regardless of its (info) severity bucket.
  if (SUCCESS_ALERT_TYPES.has(type)) return { icon: styles.iconSuccess ?? '', pill: styles.pillSuccess ?? '' };
  // "Ran out" and "Not tracked" wear the inventory panel's own dashed badges
  // rather than a severity fill — both say "there is no figure here", which is
  // a different claim from any number, and the panel already marks it that way.
  //
  // Their ICONS are pinned here too, not read off severity: the pair exists to
  // look the way the panel looks, and Not tracked is amber by severity but grey
  // on the panel (an InfoGlyph — the row states missing data, it does not warn).
  // Deriving the icon would put an amber mark next to a grey dashed pill.
  if (type === 'stock-ran-out') return { icon: styles.iconCritical ?? '', pill: styles.pillRanOut ?? '' };
  if (type === 'stock-untracked') return { icon: styles.iconInfo ?? '', pill: styles.pillUntracked ?? '' };
  switch (severity) {
    case 'critical': return { icon: styles.iconCritical ?? '', pill: styles.pillCritical ?? '' };
    case 'warning':  return { icon: styles.iconWarning ?? '',  pill: styles.pillWarning ?? '' };
    case 'info':     return { icon: styles.iconInfo ?? '',     pill: styles.pillInfo ?? '' };
  }
}


/** onKeyDown that activates a role="button" row on Enter/Space. */
function keyActivate(action: () => void) {
  return (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };
}

interface IAlertSectionProps {
  sectionId: AlertSectionId;
  /** This section's own partition of the feed — already schedule-scoped
   *  where that applies (Activities only; Issues/Deliveries & Stock carry no
   *  schedule and are never scoped out). */
  alerts: IActivityAlertItem[];
  /** How many of this section's alerts fired today — shown on the header,
   *  and kept visible even while the section is collapsed. */
  todayCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  todayOnly: boolean;
  siteTz: string;
  today: string;
  siteId: string | undefined;
  dataUpdatedAt: number;
  enteringKeys: ReadonlySet<string>;
  onOpenItem: (key: string) => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * One of the card's three collapsible categories — Activities, Issues, or
 * Deliveries & Stock. Owns its own copy of the existing filter → day-group →
 * type-group → window pipeline (previously run once for whichever tab was
 * active) so each section keeps independent scroll-window and inline
 * group-expansion state.
 *
 * Deliveries & Stock rows are non-interactive (no drawer exists for a
 * material/delivery, only its own "View in Logistics ›" link) — every other
 * row keeps opening the in-place drawer exactly as before.
 */
function AlertSection({
  sectionId, alerts, todayCount, collapsed, onToggleCollapse,
  todayOnly, siteTz, today, siteId, dataUpdatedAt, enteringKeys, onOpenItem, t,
}: IAlertSectionProps) {
  const visibleAlerts = useMemo(
    () => (todayOnly
      ? alerts.filter((alert) => isTimestampToday(alert.firedAt, siteTz, today))
      : alerts),
    [alerts, todayOnly, siteTz, today],
  );
  const groups = useMemo(() => {
    const scaffold = groupItemsByDay(visibleAlerts, (alert) => alert.firedAt, siteTz, today);
    // With the today-only quick filter on, six guaranteed-empty day sections
    // would be pure noise — the scaffold collapses to just TODAY.
    return todayOnly ? scaffold.filter((group) => group.kind === 'today') : scaffold;
  }, [visibleAlerts, todayOnly, siteTz, today]);

  // Collapse same-type alerts within each day section into single rows before
  // windowing, so a group of N alerts still counts as ONE row against the
  // page budget below.
  const dayRows = useMemo(
    () => groups.map((group) => ({ ...group, rows: groupAlertsByType(group.items) })),
    [groups],
  );

  // Windowing (shared hook, one instance per section): reset the revealed
  // page whenever WHAT's visible changes — the today toggle or fresh data —
  // but NOT on the 30s clock tick, so the reader's scroll position survives a
  // label refresh. A collapsed group counts as ONE row against the budget.
  const resetSignal = `${todayOnly}|${dataUpdatedAt}`;
  const totalVisibleCount = dayRows.reduce((sum, group) => sum + group.rows.length, 0);
  const { visibleCount, hasMore, sentinelRef } = useWindowedList(totalVisibleCount, resetSignal);

  // Window the GROUPED-AND-ROW-COLLAPSED output, trimming each day section's
  // rows to what's left of the page budget so a section can end up partially
  // shown right at the page boundary. A day that is GENUINELY empty (no
  // alerts at all) always renders — it costs no budget and shows the "no
  // alerts" line — whereas a day merely trimmed to zero by the budget is
  // withheld until the reader scrolls.
  const windowedGroups = useMemo(() => {
    let remaining = visibleCount;
    const result: (IFeedDayGroup<IActivityAlertItem> & { rows: IAlertRow[] })[] = [];
    for (const group of dayRows) {
      if (group.rows.length === 0) {
        result.push(group);
        continue;
      }
      // A non-empty day that the page budget can't afford is WITHHELD (not the
      // whole rest of the list) — `continue`, not `break`, so a genuinely-empty
      // later day still renders.
      if (remaining <= 0) continue;
      const rows = group.rows.slice(0, remaining);
      if (rows.length > 0) result.push({ ...group, rows });
      remaining -= rows.length;
    }
    return result;
  }, [dayRows, visibleCount]);

  // Group rows expanded inline to their individual members (`dayKey:type`
  // keys) — local to this section, since two sections never share a type.
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(new Set());

  return (
    <>
      <li className={styles.sectionHeading}>
        <button
          type="button"
          className={styles.sectionHeadingButton}
          aria-expanded={!collapsed}
          onClick={onToggleCollapse}
        >
          <span className={styles.sectionLabel}>{t(`dashboard.activityAlerts.sections.${sectionId}`)}</span>
          <span className={styles.sectionCount}>{todayBadge(todayCount)}</span>
          <span
            className={`${styles.sectionChevron}${!collapsed ? ` ${styles.sectionChevronOpen}` : ''}`}
            aria-hidden="true"
          >
            <ChevronDownIcon size={14} />
          </span>
        </button>
      </li>
      {!collapsed && windowedGroups.map((group) => (
        <Fragment key={group.dayKey}>
          <li className={styles.dayHeading}>
            {group.kind === 'today'
              ? t('dashboard.activityAlerts.sections.today')
              : group.kind === 'yesterday'
                ? t('dashboard.activityAlerts.sections.yesterday')
                : formatShortDate(group.dayKey, currentLang(), siteTz)}
          </li>
          {group.rows.length === 0 && (
            <li className={styles.dayEmpty}>{t('dashboard.activityAlerts.dayEmpty')}</li>
          )}
          {group.rows.map((row) => {
            if (row.kind === 'single') {
              const { alert } = row;
              const key = rowKey(alert);
              const { icon: iconClassName, pill: pillClassName } = severityClasses(alert.severity, alert.type);
              const Icon = SEVERITY_ICON[alert.severity];
              const isLogistics = isLogisticsAlertType(alert.type);
              const openThisAlert = () => onOpenItem(alertItemKey(alert));
              return (
                <li
                  key={key}
                  className={`${styles.item} ${isLogistics ? styles.itemStatic : ''} ${enteringKeys.has(key) ? styles.entering : ''}`}
                  // Deliveries & Stock rows carry no drawer to open — there is
                  // no material/delivery detail view — so the row is plain
                  // content; only its "View in Logistics ›" anchor below is
                  // interactive. Activity/issue rows keep opening their
                  // drawer in place, exactly as before.
                  role={isLogistics ? undefined : 'button'}
                  tabIndex={isLogistics ? undefined : 0}
                  onClick={isLogistics ? undefined : openThisAlert}
                  onKeyDown={isLogistics ? undefined : keyActivate(openThisAlert)}
                >
                  <span className={`${styles.icon} ${iconClassName}`}><Icon /></span>
                  <div className={styles.itemBody}>
                    <div className={styles.bodyTop}>
                      <AlertRowTitle title={alert.title} isCritical={alert.isCritical} isSafety={alert.isSafety} t={t} />
                      <AlertPill pillClassName={pillClassName} label={pillLabel(t, alert.type)} />
                    </div>
                    <div className={styles.bodySub}>
                      <span className={styles.itemSubtitle}>
                        {detailLine(alert, t, siteTz)}
                      </span>
                      {isLogistics && (
                        <Link
                          className={styles.viewInPage}
                          /* Through the shared helper, not a hand-built path:
                             this row knows its material, so the link filters
                             Logistics to it. */
                          to={alertViewHref(siteId, alert.type, '', [alert.materialName])}
                        >
                          {t('dashboard.activityAlerts.group.viewInLogistics')}
                        </Link>
                      )}
                    </div>
                  </div>
                </li>
              );
            }

            // Collapsed group row: one entry per type per day, counted
            // (never listed member-by-member) with a drill-down into the
            // filtered Activities page (or, for Deliveries & Stock, into
            // Logistics). The WHOLE row is the drill-down/expand target —
            // same destination as its "View in page ›" / "View in Logistics
            // ›" anchor — so the click target isn't just the small link.
            const groupKey = `${group.dayKey}:${row.type}`;
            const { icon: iconClassName, pill: pillClassName } = severityClasses(row.severity, row.type);
            const Icon = SEVERITY_ICON[row.severity];
            const isEntering = row.alerts.some((alert) => enteringKeys.has(rowKey(alert)));
            const isLogisticsGroup = isLogisticsAlertType(row.type);
            // Every material in the row, so the one link shows exactly the rows
            // the group is about — whether that is one material or five.
            const groupHref = alertViewHref(
              siteId,
              row.type,
              group.dayKey,
              row.alerts.map((alert) => alert.materialName),
            );
            const tipMembers = row.alerts.slice(0, GROUP_TIP_MAX);
            const isExpanded = expandedGroups.has(groupKey);
            const toggleExpanded = () => {
              setExpandedGroups((previous) => {
                const next = new Set(previous);
                if (next.has(groupKey)) next.delete(groupKey);
                else next.add(groupKey);
                return next;
              });
            };
            // Member preview shown on hover. A list layout, so it keeps its own
            // `groupTip` class inside the shared popup chrome.
            const tipContent = (
              <div className={styles.groupTip}>
                {tipMembers.map((alert) => (
                  <span key={rowKey(alert)} className={styles.groupTipRow}>
                    <span className={styles.groupTipName}>
                      <span className={styles.groupTipBullet} aria-hidden="true">-</span>
                      {alert.title}
                      {alert.isCritical && (
                        <span className={styles.groupTipZap} aria-hidden="true"><ZapIcon size={10} /></span>
                      )}
                    </span>
                    {alert.endDate && (
                      <span className={styles.groupTipDate}>
                        {formatShortDate(alert.endDate, currentLang(), siteTz)}
                      </span>
                    )}
                  </span>
                ))}
                {row.alerts.length > GROUP_TIP_MAX && (
                  <span className={styles.groupTipMore}>
                    {t('dashboard.activityAlerts.group.moreMembers', { count: row.alerts.length - GROUP_TIP_MAX })}
                  </span>
                )}
              </div>
            );
            return (
              // Hovering the row previews the members; CLICKING it expands
              // them inline as individual rows. The "View in page ›" anchor
              // remains the drill-down into the filtered page.
              // Fragment, not the tooltip, is the list parent: the expanded
              // section is a SIBLING <li>, and ui/Tooltip takes one child.
              <Fragment key={groupKey}>
                <Tooltip variant="marker" wrap side="top" align="start" sideOffset={6} content={tipContent}>
                    <li
                      className={`${styles.item} ${isEntering ? styles.entering : ''}`}
                      role="button"
                      tabIndex={0}
                      aria-expanded={isExpanded}
                      onClick={(event) => {
                        // The inner anchor owns its own clicks (plain, middle,
                        // cmd-click); bailing here keeps one click = one action.
                        if ((event.target as HTMLElement).closest('a')) return;
                        toggleExpanded();
                      }}
                      onKeyDown={keyActivate(toggleExpanded)}
                    >
                      <span className={`${styles.icon} ${iconClassName}`}><Icon /></span>
                      <div className={styles.itemBody}>
                        <div className={styles.bodyTop}>
                          <span className={styles.itemTitle}>
                            {/* A full sentence ("2 activities are overdue") so the
                                reader isn't sent to the pill at the far edge. */}
                            <span className={styles.titleText}>
                              {t(`dashboard.activityAlerts.group.sentence.${row.type}`, { count: row.alerts.length })}
                            </span>
                            <span
                              className={`${styles.expandChevron}${isExpanded ? ` ${styles.expandChevronOpen}` : ''}`}
                              aria-hidden="true"
                            >
                              <ChevronDownIcon size={14} />
                            </span>
                          </span>
                          <AlertPill pillClassName={pillClassName} label={pillLabel(t, row.type)} />
                        </div>
                        <div className={styles.bodySub}>
                          {/* An in-app link: a drill-down navigates this tab (Back
                              returns to the dashboard); a modifier- or
                              middle-click still opens a new one. */}
                          <Link className={styles.viewInPage} to={groupHref}>
                            {alertViewLabel(t, row.type)}
                          </Link>
                        </div>
                      </div>
                    </li>
                </Tooltip>
                {/* Expanded members: each alert threaded off a vertical
                    connector line under the group's icon — no severity icons,
                    no fill. Clicking a member opens its drawer like any
                    single row — except in Deliveries & Stock, where members
                    carry no drawer either. */}
                {isExpanded && (
                  <li className={styles.expandedSection}>
                    <ul className={styles.expandedList}>
                      {row.alerts.map((alert) => {
                        const openThisMember = () => onOpenItem(alertItemKey(alert));
                        return (
                          <li
                            key={rowKey(alert)}
                            className={`${styles.expandedItem} ${isLogisticsGroup ? styles.itemStatic : ''}`}
                            role={isLogisticsGroup ? undefined : 'button'}
                            tabIndex={isLogisticsGroup ? undefined : 0}
                            onClick={isLogisticsGroup ? undefined : openThisMember}
                            onKeyDown={isLogisticsGroup ? undefined : keyActivate(openThisMember)}
                          >
                            <div className={styles.bodyTop}>
                              <AlertRowTitle title={alert.title} isCritical={alert.isCritical} isSafety={alert.isSafety} t={t} />
                            </div>
                            <div className={styles.bodySub}>
                              <span className={styles.itemSubtitle}>
                                {detailLine(alert, t, siteTz)}
                              </span>
                              {/* No per-member link: the group's own "View in
                                  Logistics" is the one way out of a group, so an
                                  expanded list of five materials does not become
                                  five competing links to the same page. */}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                )}
              </Fragment>
            );
          })}
          {/* Day-closing rule below the day's last row — after EVERY day,
              including the last: it doubles as the section's closing rule, so
              the today-only view keeps a grey line between sections. */}
          <li className={styles.dayDivider} aria-hidden="true" />
        </Fragment>
      ))}
      {!collapsed && hasMore && <li ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />}
    </>
  );
}

export function ActivityAlertsCard() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  const { data, isLoading, isError, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['activity-alerts', siteId],
    queryFn: ({ signal }) => fetchActivityAlerts(siteId!, signal),
    enabled: Boolean(siteId),
    // No polling and no refetch on remount/focus: the list only changes when
    // the reader clicks refresh. The probe below tells them WHEN to.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Freshness probe — NO interval anywhere. It fetches exactly when the
  // reader causes it: on mount (page load) and when the tab regains focus
  // (TanStack's refetchOnWindowFocus listens to the visibility event). A tab
  // sitting open makes zero requests.
  const freshness = useQuery({
    queryKey: ['activity-alerts-freshness', siteId],
    queryFn: ({ signal }) => fetchAlertsFreshness(siteId!, signal),
    enabled: Boolean(siteId),
    refetchOnWindowFocus: true,
  });

  // The newest alert THIS view holds (unscoped — same universe the probe
  // reports on). ISO strings compare lexicographically.
  const viewLatestFiredAt = useMemo(() => {
    let latest: string | null = null;
    for (const alert of data?.alerts ?? []) {
      if (latest === null || alert.firedAt > latest) latest = alert.firedAt;
    }
    return latest;
  }, [data]);

  // "Changes are available" iff the server holds an alert strictly newer than
  // anything shown here — i.e. a sweep actually PRODUCED something since this
  // copy was fetched. A sweep that ran and fired nothing moves neither
  // timestamp and shows no notice.
  const changesAvailable = Boolean(
    data &&
    freshness.data?.latestFiredAt &&
    (viewLatestFiredAt === null || freshness.data.latestFiredAt > viewLatestFiredAt),
  );

  const handleRefresh = () => {
    // Refresh both: the feed (the point) and the probe (so the notice clears
    // against the same alert generation the fresh feed carries).
    void Promise.all([refetch(), freshness.refetch()]);
  };

  // Same query key as the Scheduler and the other schedule-aware dashboard
  // cards, so this shares their cache rather than adding a fetch.
  const schedulesQuery = useQuery({
    queryKey: scheduleListKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchSchedules(siteId!, signal),
    enabled: Boolean(siteId),
    staleTime: 5 * 60_000,
  });
  const activeScheduleNumber = useActiveScheduleNumber(siteId, schedulesQuery.data ?? []);

  // Ticks every 30s so the today/yesterday day-boundary (and the today-only
  // filter) self-correct across midnight. Rows carry no clock of their own —
  // every alert in a section came from the same nightly sweep, so a per-row
  // time would repeat one value down the list.
  const now = useNow(30_000);

  // The SITE's timezone — the same zone the sweep's "local midnight" and the
  // server-side day math use, and the convention every tz-aware surface here
  // follows. A Tokyo fallback only until the working site resolves.
  const siteTz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  // The current site-local calendar day, derived from the 30s clock. A STABLE
  // string within a day: recomputing it every tick is trivial, and the derived
  // memos below depend on THIS (not the raw `now` Date) so the whole
  // filter→group→row pipeline only re-runs when midnight actually rolls over.
  const today = todayKey(siteTz, now);

  const [todayOnly, setTodayOnly] = useState(false);

  // The single alert row whose drawer is open, as an IListItem key
  // (`act:<taskId>` / `iss:<issueId>`) — kept as local state, not a URL param,
  // so opening it never leaves /dashboard. AlertDrawerHost (which resolves the
  // key and mounts the heavy Activities-page queries) is rendered only while
  // this is non-null.
  const [openItemKey, setOpenItemKey] = useState<string | null>(null);
  // Stable so AlertDrawerHost's Escape-key effect doesn't resubscribe on every
  // parent re-render (this card re-renders every 30s via useNow).
  const closeDrawer = useCallback(() => setOpenItemKey(null), []);

  // Which of the card's three sections are collapsed. Per-visit component
  // state — deliberately not persisted (a fresh dashboard load always opens
  // with everything expanded).
  const [collapsedSections, setCollapsedSections] = useState<ReadonlySet<AlertSectionId>>(new Set());
  const toggleSection = (id: AlertSectionId) => {
    setCollapsedSections((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Split the feed ONCE, before the pipeline, by TYPE: logistics (Deliveries
  // & Stock) types go to their own section, `issue-*` types to Issues,
  // everything else to Activities. Each section runs its own copy of the
  // schedule-scope → filter → day-group → type-group → window pipeline below.
  const { activityAlerts, issueAlerts, logisticsAlerts } = useMemo(() => {
    const activity: IActivityAlertItem[] = [];
    const issue: IActivityAlertItem[] = [];
    const logistics: IActivityAlertItem[] = [];
    for (const alert of data?.alerts ?? []) {
      const section = sectionIdForType(alert.type);
      if (section === 'logistics') logistics.push(alert);
      else if (section === 'issues') issue.push(alert);
      else activity.push(alert);
    }
    return { activityAlerts: activity, issueAlerts: issue, logisticsAlerts: logistics };
  }, [data]);

  // The sweep covers every schedule on the site, but each row's drawer and its
  // "view in page" deep-link both resolve against the ONE work file selected in
  // the TopBar. An activity alert from another schedule would open to nothing,
  // so the Activities section scopes to the active schedule like every other
  // schedule-aware surface. Issue alerts and Deliveries & Stock alerts carry no
  // schedule (`scheduleNumber: ''`) and are never scoped out.
  const activityScoped = useMemo(
    () => activityAlerts.filter((alert) => alert.scheduleNumber === activeScheduleNumber),
    [activityAlerts, activeScheduleNumber],
  );

  // Per-section today count, shown on each section's header (visible even
  // while collapsed) — the SAME "today" the day grouping and the new-today
  // filter use.
  const activitiesTodayCount = useMemo(
    () => activityScoped.filter((alert) => isTimestampToday(alert.firedAt, siteTz, today)).length,
    [activityScoped, siteTz, today],
  );
  const issuesTodayCount = useMemo(
    () => issueAlerts.filter((alert) => isTimestampToday(alert.firedAt, siteTz, today)).length,
    [issueAlerts, siteTz, today],
  );
  const logisticsTodayCount = useMemo(
    () => logisticsAlerts.filter((alert) => isTimestampToday(alert.firedAt, siteTz, today)).length,
    [logisticsAlerts, siteTz, today],
  );
  // The header's "N new today" toggle covers the whole card now that there is
  // no active tab to scope it to — the sum of all three sections' counts.
  const newTodayCount = activitiesTodayCount + issuesTodayCount + logisticsTodayCount;

  // Enter animation: mark rows that appeared since the previous successful
  // fetch so they fade/slide in, then clear the marker once the animation
  // finishes. `enteringKeys` always holds just the LATEST batch (replaced,
  // never merged) and is cleared on its own bounded timeout, so it can't
  // accumulate across repeated refreshes.
  const previousKeysRef = useRef<Set<string> | null>(null);
  const enterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [enteringKeys, setEnteringKeys] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!data) return;
    const currentKeys = new Set(data.alerts.map(rowKey));
    const previousKeys = previousKeysRef.current;
    previousKeysRef.current = currentKeys;
    if (!previousKeys) return; // first load — nothing "enters" yet
    const newlyEntered = [...currentKeys].filter((key) => !previousKeys.has(key));
    if (newlyEntered.length === 0) return;
    setEnteringKeys(new Set(newlyEntered));
    if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
    enterTimeoutRef.current = setTimeout(() => {
      setEnteringKeys(new Set());
      enterTimeoutRef.current = null;
    }, ENTER_ANIMATION_MS);
  }, [data]);
  useEffect(() => () => {
    if (enterTimeoutRef.current) clearTimeout(enterTimeoutRef.current);
  }, []);

  let body: ReactNode;
  if (isLoading) {
    body = (
      <div className={styles.skeletonList} aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <div key={i} className={styles.skeletonRow} />)}
      </div>
    );
  } else if (isError) {
    body = <div className={styles.stateMsg}>{t('dashboard.activityAlerts.loadFailed')}</div>;
  } else {
    body = (
      <ul className={styles.list}>
        <BaseTooltip.Provider delay={300}>
          {/* No card-level day eyebrow (decided in review): each section carries
              its own day headings, so a top "Today" would mislabel the 7-day feed. */}
          <AlertSection
            sectionId="activities"
            alerts={activityScoped}
            todayCount={activitiesTodayCount}
            collapsed={collapsedSections.has('activities')}
            onToggleCollapse={() => toggleSection('activities')}
            todayOnly={todayOnly}
            siteTz={siteTz}
            today={today}
            siteId={siteId}
            dataUpdatedAt={dataUpdatedAt}
            enteringKeys={enteringKeys}
            onOpenItem={setOpenItemKey}
            t={t}
          />
          <AlertSection
            sectionId="issues"
            alerts={issueAlerts}
            todayCount={issuesTodayCount}
            collapsed={collapsedSections.has('issues')}
            onToggleCollapse={() => toggleSection('issues')}
            todayOnly={todayOnly}
            siteTz={siteTz}
            today={today}
            siteId={siteId}
            dataUpdatedAt={dataUpdatedAt}
            enteringKeys={enteringKeys}
            onOpenItem={setOpenItemKey}
            t={t}
          />
          <AlertSection
            sectionId="logistics"
            alerts={logisticsAlerts}
            todayCount={logisticsTodayCount}
            collapsed={collapsedSections.has('logistics')}
            onToggleCollapse={() => toggleSection('logistics')}
            todayOnly={todayOnly}
            siteTz={siteTz}
            today={today}
            siteId={siteId}
            dataUpdatedAt={dataUpdatedAt}
            enteringKeys={enteringKeys}
            onOpenItem={setOpenItemKey}
            t={t}
          />
        </BaseTooltip.Provider>
      </ul>
    );
  }

  return (
    <>
      <DashboardCard
        title={
          // The refresh control sits beside the card title. Alerts come from a
          // nightly sweep; this view only changes on an explicit click, so the
          // refresh affordance rides with the card's identity rather than being
          // buried in the schedule sub-line.
          <>
            {t('dashboard.activityAlerts.title')}
            <button
              type="button"
              className={`${styles.refreshButton}${isFetching ? ` ${styles.spinning}` : ''}`}
              onClick={handleRefresh}
              disabled={isFetching}
              aria-label={t('dashboard.activityAlerts.refresh')}
              title={t('dashboard.activityAlerts.refresh')}
            >
              <RefreshIcon size={14} />
            </button>
          </>
        }
        subtitle={
          // The nightly-schedule line, plus — only when the probe has seen a
          // newer sweep — the "changes are available" nudge beneath it.
          <span className={styles.subtitleStack}>
            <span>
              {t('dashboard.activityAlerts.refreshSchedule', { zone: t('dashboard.activityAlerts.zoneLabel') })}
            </span>
            {changesAvailable && (
              <button type="button" className={styles.changesAvailable} onClick={handleRefresh}>
                {t('dashboard.activityAlerts.changesAvailable')}
              </button>
            )}
          </span>
        }
        headerRight={
          <button
            type="button"
            className={`${styles.newTodayButton} ${todayOnly ? styles.newTodayActive : ''}`}
            aria-pressed={todayOnly}
            aria-label={t('dashboard.activityAlerts.newTodayFilter')}
            title={t('dashboard.activityAlerts.newTodayFilter')}
            onClick={() => setTodayOnly((prev) => !prev)}
          >
            {t('dashboard.activityAlerts.newToday', { count: newTodayCount })}
          </button>
        }
        bodyClassName={styles.body}
      >
        {body}
      </DashboardCard>
      {/* Rendered ONLY while a row's drawer is open — AlertDrawerHost mounts
          useUnifiedList's full schedule + issues queries, which must never run
          on a plain dashboard load. */}
      {siteId && openItemKey && (
        <AlertDrawerHost
          siteId={siteId}
          itemKey={openItemKey}
          onClose={closeDrawer}
        />
      )}
    </>
  );
}
